import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Collapse,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Webhook as WebhookIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { adminApi } from '../services/api.ts';

interface WebhookAuditData {
  id: number;
  event_type: string;
  agreement_id: string | null;
  participant_email: string | null;
  participant_name: string | null;
  agreement_status: string | null;
  processed_successfully: boolean;
  error_message: string | null;
  created_at: string;
  payload: any;
  link_firma: {
    id: number;
    token: string;
    estado: string;
    cliente: {
      id: number;
      nombre: string;
      email: string;
    };
  } | null;
}

interface WebhookAuditProps {
  documentoId: number;
}

const WebhookAudit: React.FC<WebhookAuditProps> = ({ documentoId }) => {
  const [webhookAudits, setWebhookAudits] = useState<WebhookAuditData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | false>('resumen');
  const [expandedPayloads, setExpandedPayloads] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadWebhookAudits();
  }, [documentoId]);

  const loadWebhookAudits = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getWebhookAudit(documentoId);
      setWebhookAudits(response.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando auditoría de webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const togglePayloadExpansion = (id: number) => {
    const newExpanded = new Set(expandedPayloads);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedPayloads(newExpanded);
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case 'AGREEMENT_COMPLETED':
      case 'AGREEMENT_WORKFLOW_COMPLETED':
        return 'success';
      case 'AGREEMENT_SIGNED':
        return 'info';
      case 'AGREEMENT_ACTION_REQUESTED':
        return 'warning';
      case 'AGREEMENT_CANCELLED':
      case 'AGREEMENT_EXPIRED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getEventTypeIcon = (eventType: string) => {
    switch (eventType) {
      case 'AGREEMENT_COMPLETED':
      case 'AGREEMENT_WORKFLOW_COMPLETED':
        return <CheckCircleIcon />;
      case 'AGREEMENT_SIGNED':
        return <WebhookIcon />;
      case 'AGREEMENT_ACTION_REQUESTED':
        return <WarningIcon />;
      case 'AGREEMENT_CANCELLED':
      case 'AGREEMENT_EXPIRED':
        return <ErrorIcon />;
      default:
        return <WarningIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatPayloadForDisplay = (payload: any) => {
    if (!payload) return 'No hay payload disponible';
    
    try {
      // Crear una copia del payload para modificar
      const formattedPayload = JSON.parse(JSON.stringify(payload));
      
      // Si hay un documento firmado con base64, truncarlo
      if (formattedPayload.agreement?.signedDocumentInfo?.document) {
        const base64Doc = formattedPayload.agreement.signedDocumentInfo.document;
        if (base64Doc && base64Doc.length > 100) {
          formattedPayload.agreement.signedDocumentInfo.document = 
            `[DOCUMENTO_BASE64_TRUNCADO: ${base64Doc.substring(0, 100)}... (${base64Doc.length} caracteres total)]`;
        }
      }
      
      // Si hay otros campos con base64 muy largos, truncarlos también
      const truncateLongBase64 = (obj: any, maxLength: number = 200) => {
        if (typeof obj === 'string' && obj.length > maxLength && obj.match(/^[A-Za-z0-9+/=]+$/)) {
          return `[BASE64_TRUNCADO: ${obj.substring(0, 50)}... (${obj.length} caracteres total)]`;
        }
        if (typeof obj === 'object' && obj !== null) {
          for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
              obj[key] = truncateLongBase64(obj[key], maxLength);
            }
          }
        }
        return obj;
      };
      
      truncateLongBase64(formattedPayload);
      
      return JSON.stringify(formattedPayload, null, 2);
    } catch (error) {
      return 'Error al formatear payload: ' + error.message;
    }
  };

  const getSuccessCount = () => {
    return webhookAudits.filter(audit => audit.processed_successfully).length;
  };

  const getErrorCount = () => {
    return webhookAudits.filter(audit => !audit.processed_successfully).length;
  };

  const getEventTypeCounts = () => {
    const counts: { [key: string]: number } = {};
    webhookAudits.forEach(audit => {
      counts[audit.event_type] = (counts[audit.event_type] || 0) + 1;
    });
    return counts;
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Cargando auditoría de webhooks...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={loadWebhookAudits}>
              Reintentar
            </Button>
          }>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (webhookAudits.length === 0) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            No hay registros de webhooks para este documento aún.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const eventTypeCounts = getEventTypeCounts();

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h3">
            Auditoría de Webhooks
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadWebhookAudits}
            size="small"
          >
            Actualizar
          </Button>
        </Box>

        {/* Resumen */}
        <Accordion expanded={expanded === 'resumen'} onChange={handleChange('resumen')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center">
              <WebhookIcon sx={{ mr: 1 }} />
              <Typography>Resumen</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`Exitosos: ${getSuccessCount()}`}
                color="success"
                variant="outlined"
              />
              <Chip
                icon={<ErrorIcon />}
                label={`Errores: ${getErrorCount()}`}
                color="error"
                variant="outlined"
              />
              <Chip
                label={`Total: ${webhookAudits.length}`}
                variant="outlined"
              />
            </Box>
            
            <Typography variant="subtitle2" mb={1}>
              Eventos por tipo:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {Object.entries(eventTypeCounts).map(([eventType, count]) => (
                <Chip
                  key={eventType}
                  icon={getEventTypeIcon(eventType)}
                  label={`${eventType}: ${count}`}
                  color={getEventTypeColor(eventType) as any}
                  size="small"
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Tabla Detallada */}
        <Accordion expanded={expanded === 'detalle'} onChange={handleChange('detalle')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center">
              <WebhookIcon sx={{ mr: 1 }} />
              <Typography>Detalle de Eventos</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Evento</TableCell>
                    <TableCell>Participante</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Documento Firmado</TableCell>
                    <TableCell>Procesado</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {webhookAudits.map((audit) => (
                    <React.Fragment key={audit.id}>
                      <TableRow>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(audit.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getEventTypeIcon(audit.event_type)}
                            label={audit.event_type}
                            color={getEventTypeColor(audit.event_type) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {audit.participant_name && (
                            <Box>
                              <Typography variant="caption" display="block">
                                {audit.participant_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {audit.participant_email}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {audit.agreement_status && (
                            <Chip
                              label={audit.agreement_status}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {audit.event_type === 'AGREEMENT_WORKFLOW_COMPLETED' && audit.processed_successfully ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Disponible"
                              color="success"
                              size="small"
                            />
                          ) : audit.event_type === 'AGREEMENT_WORKFLOW_COMPLETED' ? (
                            <Chip
                              icon={<ErrorIcon />}
                              label="Error"
                              color="error"
                              size="small"
                            />
                          ) : (
                            <Chip
                              label="N/A"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={audit.processed_successfully ? <CheckCircleIcon /> : <ErrorIcon />}
                            label={audit.processed_successfully ? 'Sí' : 'No'}
                            color={audit.processed_successfully ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => togglePayloadExpansion(audit.id)}
                          >
                            {expandedPayloads.has(audit.id) ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 0 }}>
                          <Collapse in={expandedPayloads.has(audit.id)}>
                            <Box p={2}>
                              {audit.error_message && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                  <strong>Error:</strong> {audit.error_message}
                                </Alert>
                              )}
                              <Typography variant="subtitle2" mb={1}>
                                Payload del Webhook:
                              </Typography>
                              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <pre style={{ 
                                  margin: 0, 
                                  fontSize: '0.75rem', 
                                  whiteSpace: 'pre-wrap',
                                  overflow: 'auto',
                                  maxHeight: '300px'
                                }}>
                                  {formatPayloadForDisplay(audit.payload)}
                                </pre>
                              </Paper>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default WebhookAudit;
